import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Box,
  Paper,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Fab,
} from "@mui/material";
import {
  Add,
  Delete,
  ArrowBack,
  FilterList,
  Close,
  Refresh,
  FilterAlt,
  Edit,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import EditDelete from "../../../../components/EditDelete";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import useInventory from "../../../../hooks/useInventory";
import axios from "../../../../utils/axios";
import DatePicker from "react-multi-date-picker";
import ToolBar from "../../../../components/Toolbar";
import { saveFile } from "../../../../utils/common";

function SaleSection() {
  const { getAllSale, addSale, updateSale, autoSearchUser, exportInventoryReport, isLoading } =
    useInventory();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isAdding, setIsAdding] = useState(false);
  const [materials, setMaterials] = useState([]);

  // Export State
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  // User Autocomplete State
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form State
  const [saleNo, setSaleNo] = useState("");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split("T")[0]);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([{ materialId: "", quantity: 1, unitPrice: 0, amount: 0 }]);
  const [editingId, setEditingId] = useState(null);

  // Filter State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterSaleDate, setFilterSaleDate] = useState("");
  const [filterCustomerName, setFilterCustomerName] = useState("");
  const [filterMaterialName, setFilterMaterialName] = useState("");

  // Temp/Modal Filter State
  const [tempSaleDate, setTempSaleDate] = useState("");
  const [tempCustomerName, setTempCustomerName] = useState("");
  const [tempMaterialName, setTempMaterialName] = useState("");

  const fetchSales = async (
    date = filterSaleDate,
    customer = filterCustomerName,
    material = filterMaterialName,
  ) => {
    try {
      const activeFilters = [];
      if (date) {
        activeFilters.push({
          fieldName: "saleDate",
          fieldValue: date,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (customer.trim()) {
        activeFilters.push({
          fieldName: "customerName",
          fieldValue: customer.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (material.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: material.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }

      const payload = {
        pagination: { pageNumber: paginationModel.page + 1, pageSize: paginationModel.pageSize },
        filters: activeFilters,
        sortFields: [{ field: "saleId", sort: "desc" }],
      };
      const res = await getAllSale(payload);
      if (res) {
        setRows(res.data || []);
        setRowCount(res.paging?.totalNumberOfRecords || 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMaterials = async () => {
    try {
      const res = await axios.post("/material/list", {
        pagination: { pageNumber: 1, pageSize: 1000 },
        filters: [],
        sortFields: [],
      });
      setMaterials(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchSales();
  }, [paginationModel, filterSaleDate, filterCustomerName, filterMaterialName]);

  useEffect(() => {
    if (isAdding) {
      fetchMaterials();
    }
  }, [isAdding]);

  const handleUserSearch = async (val) => {
    if (!val || val.length < 2) return;
    setUserLoading(true);
    try {
      const results = await autoSearchUser(val);
      setUserOptions(results);
    } catch (e) {
      console.error(e);
    } finally {
      setUserLoading(false);
    }
  };

  const handleAddItemRow = () => {
    setItems([...items, { materialId: "", quantity: 1, unitPrice: 0, amount: 0 }]);
  };

  const handleRemoveItemRow = (index) => {
    const updated = items.filter((_, idx) => idx !== index);
    setItems(updated);
  };

  const handleItemChange = (index, field, value) => {
    const updated = items.map((item, idx) => {
      if (idx === index) {
        const newItem = { ...item, [field]: value };
        if (field === "quantity" || field === "unitPrice") {
          const qty = field === "quantity" ? parseFloat(value) || 0 : item.quantity;
          const price = field === "unitPrice" ? parseFloat(value) || 0 : item.unitPrice;
          newItem.amount = qty * price;
        }
        return newItem;
      }
      return item;
    });
    setItems(updated);
  };

  const dispatch = useDispatch();

  const handleEdit = (row) => {
    setSaleNo(row.saleNo || "");
    setSaleDate(row.saleDate ? row.saleDate.split(" ")[0] : new Date().toISOString().split("T")[0]);
    setSelectedUser(
      row.customerId ? { radUserId: row.customerId, userName: row.customerName } : null,
    );
    setInvoiceNo(row.invoiceNo || "");
    setRemarks(row.remarks || "");
    setItems(
      row.items && row.items.length > 0
        ? row.items.map((i) => ({
            materialId: i.materialId,
            quantity: i.quantity,
            unitPrice: i.unitPrice,
            amount: i.quantity * i.unitPrice,
          }))
        : [{ materialId: "", quantity: 1, unitPrice: 0, amount: 0 }],
    );
    setEditingId(row.saleId);
    setIsAdding(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      dispatch(showErrorSnackbar("Customer is required"));
      return;
    }
    const hasInvalidItem = items.some((item) => !item.materialId || item.quantity <= 0);
    if (hasInvalidItem) {
      dispatch(
        showErrorSnackbar("Please select a material and enter a valid quantity for all rows."),
      );
      return;
    }

    try {
      const payload = {
        saleNo,
        saleDate,
        customerId: parseInt(selectedUser.radUserId),
        invoiceNo,
        remarks,
        items: items.map((i) => ({
          materialId: parseInt(i.materialId),
          quantity: parseFloat(i.quantity),
          unitPrice: parseFloat(i.unitPrice),
        })),
      };
      if (editingId) {
        await updateSale(editingId, payload);
        dispatch(showSuccessSnackbar("Sale updated successfully!"));
      } else {
        await addSale(payload);
        dispatch(showSuccessSnackbar("Sale added successfully!"));
      }
      setIsAdding(false);
      setEditingId(null);
      fetchSales();
      // Reset form
      setSaleNo("");
      setSelectedUser(null);
      setInvoiceNo("");
      setRemarks("");
      setItems([{ materialId: "", quantity: 1, unitPrice: 0, amount: 0 }]);
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to save sale"));
    }
  };

  const handleDownloadFile = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      const activeFilters = [];
      if (filterSaleDate) {
        activeFilters.push({
          fieldName: "saleDate",
          fieldValue: filterSaleDate,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterCustomerName.trim()) {
        activeFilters.push({
          fieldName: "customerName",
          fieldValue: filterCustomerName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterMaterialName.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: filterMaterialName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      const payload = {
        filters: activeFilters,
      };

      const resBlob = await exportInventoryReport("sale", fileType, payload);
      const filename = `sale_list_${new Date().toISOString().slice(0, 10)}.${fileType}`;
      await saveFile(resBlob, filename);
      dispatch(showSuccessSnackbar(`Report exported successfully as ${fileType.toUpperCase()}!`));
    } catch (err) {
      console.error(err);
      dispatch(showErrorSnackbar("Failed to export report"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };

  const handleApplyFilter = () => {
    setFilterSaleDate(tempSaleDate);
    setFilterCustomerName(tempCustomerName);
    setFilterMaterialName(tempMaterialName);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleResetFilter = () => {
    setFilterSaleDate("");
    setFilterCustomerName("");
    setFilterMaterialName("");
    setTempSaleDate("");
    setTempCustomerName("");
    setTempMaterialName("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleRefresh = () => {
    fetchSales();
  };

  const activeFiltersCount =
    (filterSaleDate ? 1 : 0) + (filterCustomerName ? 1 : 0) + (filterMaterialName ? 1 : 0);

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <EditDelete
          rowId={params.row.saleId}
          editPage={() => handleEdit(params.row)}
          menus={["edit"]}
        />
      ),
    },
    { field: "saleNo", headerName: "Sale No", width: 180 },
    { field: "saleDate", headerName: "Sale Date", width: 180 },
    { field: "customerName", headerName: "Customer Name", width: 200 },
    {
      field: "materialName",
      headerName: "Material Name",
      width: 220,
      valueGetter: (value, row) => {
        if (!row?.items || row.items.length === 0) return "";
        return row.items
          .map((item) => item.materialName || "")
          .filter(Boolean)
          .join(", ");
      },
    },
    { field: "invoiceNo", headerName: "Invoice No", width: 140 },
    { field: "totalAmount", headerName: "Total Amount", width: 150, type: "number" },
    { field: "remarks", headerName: "Remarks", width: 220 },
  ];

  if (isAdding) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h5">{editingId ? "Edit Sale" : "Add Sale"}</Typography>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              setIsAdding(false);
              setEditingId(null);
              setSaleNo("");
              setSelectedUser(null);
              setInvoiceNo("");
              setRemarks("");
              setItems([{ materialId: "", quantity: 1, unitPrice: 0, amount: 0 }]);
            }}
          >
            Back to List
          </Button>
        </Box>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <DatePicker
                containerStyle={{ width: "100%" }}
                portal
                zIndex={10000}
                value={saleDate}
                onChange={(date) => setSaleDate(date ? date.format("YYYY-MM-DD") : "")}
                format="YYYY/MM/DD"
                render={
                  <TextField
                    fullWidth
                    label="Sale Date"
                    required
                    InputLabelProps={{ shrink: !!saleDate }}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={userOptions}
                getOptionLabel={(option) => option.userName || ""}
                loading={userLoading}
                value={selectedUser}
                onChange={(e, newVal) => setSelectedUser(newVal)}
                onInputChange={(e, val) => handleUserSearch(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Customer"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {userLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Invoice No"
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                multiline
                rows={1}
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>

            {/* Items Grid */}
            <Grid item xs={12}>
              <Typography variant="h6" mb={2}>
                Material Items
              </Typography>
              <TableContainer component={Paper} elevation={1}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="40%">Material</TableCell>
                      <TableCell width="20%">Quantity</TableCell>
                      <TableCell width="20%">Unit Price</TableCell>
                      <TableCell width="15%">Amount</TableCell>
                      <TableCell width="5%">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Select
                            fullWidth
                            value={item.materialId}
                            onChange={(e) => handleItemChange(index, "materialId", e.target.value)}
                            displayEmpty
                            required
                          >
                            <MenuItem value="" disabled>
                              Select Material
                            </MenuItem>
                            {materials.map((m) => (
                              <MenuItem key={m.materialId} value={m.materialId}>
                                {m.materialName} ({m.uom})
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            type="number"
                            inputProps={{ min: "1", step: "1" }}
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            type="number"
                            inputProps={{ min: "0", step: "0.01" }}
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" align="right" sx={{ mt: 2 }}>
                            {item.amount.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveItemRow(index)}
                            disabled={items.length <= 1}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box mt={2}>
                <Button startIcon={<Add />} variant="outlined" onClick={handleAddItemRow}>
                  Add Row
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                size="large"
              >
                {isLoading ? "Saving..." : "Save Sale"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h5">Sales History</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <ToolBar
            addPage={() => setIsAdding(true)}
            refreshPage={handleRefresh}
            openFilterDialog={() => setIsFilterOpen(true)}
            downloadFile={handleDownloadFile}
            isLoadingDownload={isDownloading}
            downloadFileType={downloadType}
          />
        </Box>
      </Box>
      <div style={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.saleId}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={isLoading}
          disableRowSelectionOnClick
        />
      </div>

      {/* Advance Filter Dialog */}
      <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">Advance Filter</Typography>
          <IconButton onClick={() => setIsFilterOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Customer Name"
                value={tempCustomerName}
                onChange={(e) => setTempCustomerName(e.target.value)}
                placeholder="Search customer name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Material Name"
                value={tempMaterialName}
                onChange={(e) => setTempMaterialName(e.target.value)}
                placeholder="Search material name"
              />
            </Grid>
            <Grid item xs={12}>
              <DatePicker
                containerStyle={{ width: "100%" }}
                portal
                zIndex={10000}
                range
                value={tempSaleDate ? tempSaleDate.split(",") : []}
                onChange={(dates) => {
                  if (dates && dates.length > 0) {
                    const formatted = dates.map((d) => d.format("YYYY-MM-DD")).join(",");
                    setTempSaleDate(formatted);
                  } else {
                    setTempSaleDate("");
                  }
                }}
                format="YYYY/MM/DD"
                render={
                  <TextField
                    fullWidth
                    label="Sale Date"
                    variant="outlined"
                    InputLabelProps={{ shrink: !!tempSaleDate }}
                  />
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleResetFilter} variant="outlined" startIcon={<Refresh />}>
            Reset
          </Button>
          <Button
            onClick={handleApplyFilter}
            variant="contained"
            color="primary"
            startIcon={<FilterAlt />}
          >
            filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SaleSection;
